/**
 * Utilities for persisting email template generation state per dealer in localStorage
 */

export interface SocketMessageItem {
  eventName: string;
  data: any;
  timestamp: number;
}

export interface PersistedGenerationState {
  generateInProgress: boolean;
  dealerId: string;
  progress: {
    current: number;
    total: number;
  };
  socketMessages: SocketMessageItem[]; // Last few socket messages for display
  lastSocketMessage: SocketMessageItem | null; // Most recent message for modal display
  timestamp: number; // To validate freshness
}

const STORAGE_KEY_PREFIX = 'email_generation_state_';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Get storage key for a specific dealer
 */
function getStorageKey(dealerId: string): string {
  return `${STORAGE_KEY_PREFIX}${dealerId}`;
}

/**
 * Save generation state to localStorage for a specific dealer
 */
export function saveGenerationState(
  dealerId: string,
  state: Omit<PersistedGenerationState, 'timestamp' | 'dealerId'>
): void {
  try {
    if (!dealerId) return;
    
    const persistedState: PersistedGenerationState = {
      ...state,
      dealerId,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(getStorageKey(dealerId), JSON.stringify(persistedState));
  } catch (error) {
    console.error('Failed to save generation state to localStorage:', error);
  }
}

/**
 * Load generation state from localStorage for a specific dealer
 * Returns null if no valid state found or state is too old
 */
export function loadGenerationState(dealerId: string): PersistedGenerationState | null {
  try {
    if (!dealerId) return null;
    
    const stored = localStorage.getItem(getStorageKey(dealerId));
    if (!stored) return null;
    
    const state: PersistedGenerationState = JSON.parse(stored);
    
    // Validate state
    if (
      !state ||
      state.dealerId !== dealerId ||
      typeof state.generateInProgress !== 'boolean' ||
      !state.progress ||
      typeof state.progress.current !== 'number' ||
      typeof state.progress.total !== 'number'
    ) {
      clearGenerationState(dealerId);
      return null;
    }
    
    // Ensure arrays exist (for backward compatibility)
    if (!Array.isArray(state.socketMessages)) {
      state.socketMessages = [];
    }
    
    // Check if state is too old (stale)
    const age = Date.now() - (state.timestamp || 0);
    if (age > MAX_AGE_MS) {
      clearGenerationState(dealerId);
      return null;
    }
    
    // Only return if generation was actually in progress
    if (!state.generateInProgress) {
      return null;
    }
    
    return state;
  } catch (error) {
    console.error('Failed to load generation state from localStorage:', error);
    return null;
  }
}

/**
 * Clear generation state for a specific dealer
 */
export function clearGenerationState(dealerId: string): void {
  try {
    if (!dealerId) return;
    localStorage.removeItem(getStorageKey(dealerId));
  } catch (error) {
    console.error('Failed to clear generation state from localStorage:', error);
  }
}

/**
 * Clear all generation states (for cleanup)
 */
export function clearAllGenerationStates(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Failed to clear all generation states:', error);
  }
}

/**
 * Update only the progress for a specific dealer (keeps other fields intact)
 */
export function updateGenerationProgress(
  dealerId: string,
  progress: { current: number; total: number }
): void {
  try {
    if (!dealerId) return;
    
    const existing = loadGenerationState(dealerId);
    if (!existing) {
      // If no existing state, create a new one with progress
      saveGenerationState(dealerId, {
        generateInProgress: true,
        progress,
        socketMessages: [],
        lastSocketMessage: null,
      });
      return;
    }
    
    // Update progress while keeping other fields
    saveGenerationState(dealerId, {
      generateInProgress: existing.generateInProgress,
      progress,
      socketMessages: existing.socketMessages || [],
      lastSocketMessage: existing.lastSocketMessage || null,
    });
  } catch (error) {
    console.error('Failed to update generation progress:', error);
  }
}

/**
 * Update socket messages for a specific dealer (keeps last 10 messages)
 */
export function updateSocketMessages(
  dealerId: string,
  newMessage: SocketMessageItem
): void {
  try {
    if (!dealerId) return;
    
    const existing = loadGenerationState(dealerId);
    const currentMessages = existing?.socketMessages || [];
    
    // Keep last 10 messages only
    const updatedMessages = [...currentMessages, newMessage].slice(-10);
    
    if (!existing) {
      saveGenerationState(dealerId, {
        generateInProgress: true,
        progress: { current: 0, total: 1 },
        socketMessages: updatedMessages,
        lastSocketMessage: newMessage,
      });
      return;
    }
    
    saveGenerationState(dealerId, {
      generateInProgress: existing.generateInProgress,
      progress: existing.progress,
      socketMessages: updatedMessages,
      lastSocketMessage: newMessage,
    });
  } catch (error) {
    console.error('Failed to update socket messages:', error);
  }
}
